/**
 * Botón de notificaciones en la Topbar.
 *
 * - Pinta un icono Bell con un badge cuando hay no-leídas (unreadCount).
 * - Polling cada 30 s al endpoint /notifications?unreadOnly=true&limit=1.
 * - Click abre un popover con <NotificationsList /> (componente ya existente
 *   creado por otro agente para integrar el follow-up IA).
 */

import { useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { fetchUnreadCount } from '../../lib/notifications'
import NotificationsList from './NotificationsList'

const POLL_INTERVAL_MS = 30_000

interface Props {
  isLight: boolean
}

export default function NotificationsBell({ isLight }: Props) {
  const { t } = useTranslation('notifications')
  const [open, setOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Cierre por click fuera.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Polling de unreadCount. Se ejecuta inmediatamente y luego cada 30 s.
  // Polling unificado: 30 s normalmente, 3 s mientras el popover está abierto.
  // Se PAUSA cuando la pestaña está oculta para no quemar cuota.
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      try {
        const n = await fetchUnreadCount()
        if (!cancelled) setUnreadCount(n)
      } catch {
        // silencioso: si la API falla puntualmente, mantenemos el valor previo.
      }
    }
    void tick()
    let id: number | null = null
    const startInterval = () => {
      if (id !== null) window.clearInterval(id)
      const ms = open ? 3_000 : POLL_INTERVAL_MS
      id = window.setInterval(() => {
        if (document.visibilityState !== 'visible') return
        void tick()
      }, ms)
    }
    startInterval()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void tick()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelled = true
      if (id !== null) window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [open])

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'relative w-9 h-9 grid place-items-center rounded-full transition',
          isLight
            ? 'hover:bg-slate-200/60 text-slate-500'
            : 'hover:bg-slate-800/60 text-slate-300',
        ].join(' ')}
        aria-label={t('bell.ariaLabel')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center"
            aria-label={t('bell.unreadCount', { count: unreadCount })}
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-h-[32rem] overflow-y-auto z-50 animate-slideUp"
          role="dialog"
          aria-label={t('panel.ariaLabel')}
        >
          {/* `onChanged` refresca el contador en cuanto se vacía la lista, sin
              esperar al siguiente sondeo. */}
          <NotificationsList onChanged={() => setUnreadCount(0)} />
        </div>
      )}
    </div>
  )
}
